import Button from '@material-ui/core/Button';
import CardActions from '@material-ui/core/CardActions';
import Grid from '@material-ui/core/Grid';
import FilterCardAngle from 'Components/Editor/Details/Filters/FilterCardAngle';
import FilterCardArea from 'Components/Editor/Details/Filters/FilterCardArea';
import FilterCardBrands from 'Components/Editor/Details/Filters/FilterCardBrands';
import FilterCardConfidence from 'Components/Editor/Details/Filters/FilterCardConfidence';
import FilterCardPlacements from 'Components/Editor/Details/Filters/FilterCardPlacements';
import FilterCardPosition from 'Components/Editor/Details/Filters/FilterCardPosition';
import FilterCardTime from 'Components/Editor/Details/Filters/FilterCardTime';
import FilterCardPerspective from 'Components/Editor/Details/Filters/FilterCardPerspective';
import ChangeAllPlacementModal from 'Components/Editor/Details/ChangeAllPlacementModal';
import { UseVideoPlayerInstance } from 'Hooks/Editor/useVideoPlayerInstance';
import { ReactElement, useMemo, useState } from 'react';

interface FiltersDetailsProps {
	instance: UseVideoPlayerInstance;
}

const FiltersDetails = (props: FiltersDetailsProps): ReactElement => {
	const { filters, setFilters } = props.instance;
	const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);
	const selectedBrands = useMemo(
		() => (filters.brand === null ? null : props.instance.brands.filter((x) => filters.brand?.brandIds.includes(x.id))),
		[filters.brand, props.instance.brands]
	);
	const selectedLogoIds =
		filters.logoOrText?.logoIds ?? selectedBrands?.flatMap((x) => x.logos.map((logo) => logo.id)) ?? [];
	const selectedTextIds =
		filters.logoOrText?.textIds ?? selectedBrands?.flatMap((x) => x.texts.map((text) => text.id)) ?? [];
	const selectedDetections = useMemo(
		() => props.instance.detections.filter((x) => props.instance.selectedDetectionIds.includes(x.id)),
		[props.instance.detections, props.instance.selectedDetectionIds]
	);

	const handleOpenModalButtonClicked = (): void => {
		setIsChangeModalOpen((prev) => !prev);
	};

	const handleChangeAllPlacementModalClose = (): void => {
		setIsChangeModalOpen(false);
	};

	const handleDetectionsChanged = (): void => {
		setIsChangeModalOpen(false);
		props.instance.clearDetections();
	};

	return (
		<>
			<Grid container={true} spacing={2}>
				<Grid item={true} xs={12} md={6} lg={4}>
					<FilterCardPlacements
						placements={props.instance.placements}
						selectedIds={filters.placement?.placementIds ?? null}
						onSelectedIdsChanged={(ids) =>
							setFilters((prev) => ({ ...prev, placement: ids === null ? null : { placementIds: ids } }))
						}
						selectedDetections={selectedDetections}
					/>
				</Grid>
				<Grid item={true} xs={12} md={6} lg={4}>
					<FilterCardBrands
						brands={props.instance.brands}
						selectedBrands={selectedBrands}
						onSelectedBrandsChanged={(brands) =>
							setFilters((prev) => ({
								...prev,
								brand: brands === null ? null : { brandIds: brands.map((x) => x.id) },
								logoOrText: null,
							}))
						}
						selectedTexts={selectedTextIds}
						onSelectedTextsChanged={(ids) =>
							setFilters((prev) => ({
								...prev,
								logoOrText: { logoIds: prev.logoOrText?.logoIds ?? selectedLogoIds, textIds: ids ?? [] },
							}))
						}
						selectedLogos={selectedLogoIds}
						onSelectedLogosChanged={(ids) =>
							setFilters((prev) => ({
								...prev,
								logoOrText: { textIds: prev.logoOrText?.textIds ?? selectedTextIds, logoIds: ids ?? [] },
							}))
						}
						selectedDetections={selectedDetections}
					/>
				</Grid>
				<Grid item={true} xs={12} md={6} lg={4}>
					<FilterCardAngle
						angle={filters.angle}
						onAngleChanged={(angle) => setFilters((prev) => ({ ...prev, angle }))}
						selectedDetections={selectedDetections}
					/>
				</Grid>
				<Grid item={true} xs={12} md={6} lg={4}>
					<FilterCardArea
						area={filters.area}
						onAreaChanged={(area) => setFilters((prev) => ({ ...prev, area }))}
						selectedDetections={selectedDetections}
					/>
				</Grid>
				<Grid item={true} xs={12} md={6} lg={4}>
					<FilterCardPosition
						position={filters.position}
						onPositionChange={(position) => setFilters((prev) => ({ ...prev, position }))}
						instance={props.instance}
						selectedDetections={selectedDetections}
					/>
				</Grid>
				<Grid item={true} xs={12} md={6} lg={4}>
					<FilterCardTime
						time={filters.time}
						onTimeChanged={(time) => setFilters((prev) => ({ ...prev, time }))}
						video={props.instance.videoNode}
					/>
				</Grid>
				<Grid item={true} xs={12} md={6} lg={4}>
					<FilterCardPerspective
						perspective={filters.perspective}
						onPerspectiveChanged={(perspective) => setFilters((prev) => ({ ...prev, perspective }))}
						selectedDetections={selectedDetections}
					/>
				</Grid>
				<Grid item={true} xs={12} md={6} lg={4}>
					<FilterCardConfidence
						confidence={filters.confidence}
						onConfidenceChanged={(confidence) => setFilters((prev) => ({ ...prev, confidence }))}
						selectedDetections={selectedDetections}
					/>
				</Grid>
			</Grid>
			<br />
			<CardActions>
				<Button variant="contained" onClick={handleOpenModalButtonClicked} disabled={props.instance.isFiltering}>
					Open change all details modal
				</Button>
			</CardActions>
			<ChangeAllPlacementModal
				open={isChangeModalOpen}
				isFiltering={props.instance.isFiltering}
				request={filters}
				placements={props.instance.placements}
				videoId={props.instance.video?.id ?? 0}
				onClose={handleChangeAllPlacementModalClose}
				onDetectionChanged={handleDetectionsChanged}
			/>
		</>
	);
};

export default FiltersDetails;
